/**
 * 🔥 مستودع باقات Firestore
 *
 * المسار الوحيد:
 *   {firestoreCollections.venues}/{venueId}/packages/{packageKey}
 *
 * الكتابات تمر حصراً عبر Cloud Functions؛ القراءات من المسار القياسي أعلاه.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type Query,
} from 'firebase/firestore';

import { db } from '@infrastructure/firebase/config';
import { firestoreCollections } from '@infrastructure/firebase/collections';
import { Package } from '@domain/entities/Package';
import type {
  IPackageRepository,
  PackageFilter,
} from '@domain/repositories/IPackageRepository';
import {
  packageFromDocument,
} from '@/infrastructure/firebase/mappers/PackageMapper';
import {
  callBulkUpdateVenuePackagePrices,
  callCreateVenuePackage,
  callDeleteVenuePackage,
  callUpdateVenuePackage,
  type VenuePackageMutationData,
} from '@infrastructure/firebase/functions';

export class FirestorePackageRepository implements IPackageRepository {
  private packagesRef(venueId: string) {
    return collection(
      db,
      firestoreCollections.venues,
      venueId,
      firestoreCollections.packages
    );
  }

  private packageRef(venueId: string, key: string) {
    return doc(
      db,
      firestoreCollections.venues,
      venueId,
      firestoreCollections.packages,
      key
    );
  }

  async getByKey(
    venueId: string,
    key: string
  ): Promise<Package | null> {
    const snapshot = await getDoc(this.packageRef(venueId, key));
    if (!snapshot.exists()) return null;
    return packageFromDocument(snapshot.id, venueId, snapshot.data());
  }

  async listByVenue(venueId: string): Promise<Package[]> {
    const snapshot = await getDocs(this.packagesRef(venueId));
    return snapshot.docs.map((item) =>
      packageFromDocument(item.id, venueId, item.data())
    );
  }

  async query(
    venueId: string,
    filter: PackageFilter
  ): Promise<Package[]> {
    let packagesQuery: Query<DocumentData> =
      this.packagesRef(venueId);

    if (filter.sectionMode) {
      packagesQuery = query(
        packagesQuery,
        where('sectionMode', '==', filter.sectionMode)
      );
    }
    if (filter.dayType) {
      packagesQuery = query(
        packagesQuery,
        where('dayType', '==', filter.dayType)
      );
    }
    if (filter.season) {
      packagesQuery = query(
        packagesQuery,
        where('season', '==', filter.season)
      );
    }
    if (filter.hasIncludedServices !== undefined) {
      packagesQuery = query(
        packagesQuery,
        where(
          'hasIncludedServices',
          '==',
          filter.hasIncludedServices
        )
      );
    }
    if (filter.guestCount !== undefined) {
      packagesQuery = query(
        packagesQuery,
        where('capacityMax', '>=', filter.guestCount)
      );
    }
    if (
      filter.guestCountMen !== undefined &&
      filter.guestCountWomen !== undefined
    ) {
      packagesQuery = query(
        packagesQuery,
        where('capacityMenMax', '>=', filter.guestCountMen),
        where('capacityWomenMax', '>=', filter.guestCountWomen)
      );
    }

    const snapshot = await getDocs(packagesQuery);
    return snapshot.docs.map((item) =>
      packageFromDocument(item.id, venueId, item.data())
    );
  }

  async create(pkg: Package): Promise<void> {
    await callCreateVenuePackage({
      venueId: pkg.props.venueId,
      package: packageToMutationData(pkg),
    });
  }

  /** يرسل التعديل الكامل للخادم مع مفتاح الباقة الحالي. */
  async save(pkg: Package): Promise<void> {
    await callUpdateVenuePackage({
      venueId: pkg.props.venueId,
      oldKey: pkg.key,
      package: packageToMutationData(pkg),
    });
  }

  /**
   * نقل ذري عندما تتغير عوامل الهوية.
   *
   * الخادم يمنع التصادم وينقل المستند داخل transaction واحدة.
   */
  async move(
    venueId: string,
    oldKey: string,
    pkg: Package
  ): Promise<void> {
    await callUpdateVenuePackage({
      venueId,
      oldKey,
      package: packageToMutationData(pkg),
    });
  }

  async saveBatch(packages: Package[]): Promise<void> {
    if (packages.length === 0) return;

    const venueIds = new Set(packages.map((pkg) => pkg.props.venueId));
    if (venueIds.size !== 1) {
      throw new Error('التحديث الجماعي يجب أن يخص قاعة واحدة');
    }

    await callBulkUpdateVenuePackagePrices({
      venueId: packages[0]!.props.venueId,
      prices: packages.map((pkg) => ({
        key: pkg.key,
        price: pkg.props.price.toSAR(),
      })),
    });
  }

  async delete(venueId: string, key: string): Promise<void> {
    await callDeleteVenuePackage({ venueId, key });
  }

  async exists(venueId: string, key: string): Promise<boolean> {
    const snapshot = await getDoc(this.packageRef(venueId, key));
    return snapshot.exists();
  }

  subscribeToVenue(
    venueId: string,
    callback: (packages: Package[]) => void,
    onError?: (err: Error) => void
  ): () => void {
    return onSnapshot(
      this.packagesRef(venueId),
      (snapshot) => {
        try {
          const packages = snapshot.docs.map((item) =>
            packageFromDocument(item.id, venueId, item.data())
          );
          callback(packages);
        } catch (error) {
          const normalizedError =
            error instanceof Error
              ? error
              : new Error('تعذّر قراءة البكجات');
          // eslint-disable-next-line no-console
          console.error(
            '[PackageRepo] فشل تحويل مستند باقة:',
            normalizedError
          );
          onError?.(normalizedError);
        }
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[PackageRepo] live stream error:', error);
        onError?.(error);
      }
    );
  }
}

export const packageRepository =
  new FirestorePackageRepository();

function packageToMutationData(pkg: Package): VenuePackageMutationData {
  const p = pkg.props;
  return {
    name: p.name,
    price: p.price.toSAR(),
    ...(p.sectionMode === 'both'
      ? {
          capacityMenMin: p.capacityMenMin!,
          capacityMenMax: p.capacityMenMax!,
          capacityWomenMin: p.capacityWomenMin!,
          capacityWomenMax: p.capacityWomenMax!,
        }
      : {
          capacityMin: p.capacityMin,
          capacityMax: p.capacityMax,
        }),
    sectionMode: p.sectionMode,
    dayType: p.dayType,
    season: p.season,
    hasIncludedServices: p.hasIncludedServices,
    ...(p.includedServicesText
      ? { includedServicesText: p.includedServicesText }
      : {}),
    ...(p.includedServices ? { includedServices: p.includedServices } : {}),
    ...(p.includedServicesMenText
      ? { includedServicesMenText: p.includedServicesMenText }
      : {}),
    ...(p.includedServicesMen
      ? { includedServicesMen: p.includedServicesMen }
      : {}),
    ...(p.includedServicesWomenText
      ? { includedServicesWomenText: p.includedServicesWomenText }
      : {}),
    ...(p.includedServicesWomen
      ? { includedServicesWomen: p.includedServicesWomen }
      : {}),
    ...(p.notes ? { notes: p.notes } : {}),
  };
}
